/**
 * Indie Chat — shared types, data, and inline SVG icons
 * Extracted from Figma design export (fusion-starter-6c1)
 */
import React from 'react';

// ─── Types ──────────────────────────────────────────────────────────────────

export type LeftTab = 'history' | 'prompts' | 'files';
/** Structured tables behind a file export (e.g. a sales-target plan). */
export interface OrbitExportTables {
  title?: string;
  /** totals: true asks the file writer to append a TOTAL row of column sums. */
  tables: Array<{ name?: string; columns: string[]; rows: unknown[][]; totals?: boolean }>;
}

export type RightTab = 'actions' | 'artifacts' | 'files';

export type BadgeStyle = 'amber' | 'red' | 'purple' | 'green';

export interface ActionCard {
  num: number;
  title: string;
  subtitle: string;
  badge: string;
  badgeStyle: BadgeStyle;
}

export interface ArtifactItem {
  id: number;
  title: string;
  subtitle: string;
  iconBg: string;
  icon: React.ReactNode;
}

export interface Prompt {
  id: number;
  label: string;
  iconBg: string;
  icon: React.ReactNode;
  iconName?: string | null;
  iconStrokeColor?: string | null;
}

export interface SuggestionCard {
  id: number;
  title: string;
  description: string;
  badge: string;
  iconBg?: string;
  badgeBg?: string;
  badgeColor?: string;
  icon: React.ReactNode;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Structured tables for Excel / PDF / slides builds (done event or content_json). */
  export_tables?: OrbitExportTables;
  metadata?: {
    orbitErrorCode?: string;
    orbitErrorKind?: 'service_unavailable' | 'generic';
    attachments?: Array<{
      attachmentId: string;
      attachmentLabel: string;
      attachmentType: string;
      attachmentTypeDisplay: string;
      filename: string;
    }>;
  };
}

// ─── Badge styles ────────────────────────────────────────────────────────────

export const badgeStyles: Record<BadgeStyle | string, string> = {
  amber: 'bg-[#FFFAEB] text-[#B54708]',
  red: 'bg-[#FEF3F2] text-[#B42318]',
  purple: 'bg-[#F9F5FF] text-[#53389E]',
  green: 'bg-[#ECFDF3] text-[#079455]',
};

// ─── Static data ─────────────────────────────────────────────────────────────

export const m1Prompts: Prompt[] = [
  {
    id: 1,
    label: "What's working well & what needs attention?",
    iconBg: '#FEF3F2',
    iconName: 'warning',
    iconStrokeColor: '#B42318',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M2.16656 7.58344C2.06406 7.58379 1.96356 7.55505 1.87674 7.50056C1.78993 7.44606 1.72035 7.36805 1.67611 7.27559C1.63186 7.18313 1.61476 7.08001 1.62679 6.97822C1.63882 6.87642 1.67948 6.78013 1.74406 6.70053L7.10656 1.17553C7.14679 1.12909 7.2016 1.09772 7.26201 1.08655C7.32242 1.07538 7.38483 1.08507 7.439 1.11405C7.49317 1.14302 7.53588 1.18955 7.56012 1.246C7.58436 1.30245 7.58869 1.36546 7.57239 1.42469L6.53239 4.68553C6.50173 4.7676 6.49143 4.85589 6.50238 4.94282C6.51333 5.02975 6.54521 5.11273 6.59528 5.18463C6.64534 5.25654 6.7121 5.31522 6.78984 5.35565C6.86757 5.39609 6.95394 5.41706 7.04156 5.41678H10.8332C10.9357 5.41643 11.0362 5.44517 11.123 5.49966C11.2099 5.55416 11.2794 5.63217 11.3237 5.72463C11.3679 5.81709 11.385 5.92021 11.373 6.022C11.361 6.1238 11.3203 6.22009 11.2557 6.29969L5.89323 11.8247C5.853 11.8711 5.79819 11.9025 5.73778 11.9137C5.67737 11.9248 5.61496 11.9151 5.56079 11.8862C5.50662 11.8572 5.46391 11.8107 5.43967 11.7542C5.41543 11.6978 5.4111 11.6348 5.42739 11.5755L6.46739 8.31469C6.49806 8.23262 6.50836 8.14433 6.49741 8.0574C6.48646 7.97047 6.45458 7.88749 6.40451 7.81559C6.35445 7.74368 6.28768 7.685 6.20995 7.64456C6.13222 7.60413 6.04584 7.58316 5.95823 7.58344H2.16656Z" stroke="#B42318" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 2,
    label: "What should I chase or mark down?",
    iconBg: '#FFFAEB',
    iconName: 'shopping',
    iconStrokeColor: '#B54708',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M3.25 1.0835L1.625 3.25016V10.8335C1.625 11.1208 1.73914 11.3964 1.9423 11.5995C2.14547 11.8027 2.42102 11.9168 2.70833 11.9168H10.2917C10.579 11.9168 10.8545 11.8027 11.0577 11.5995C11.2609 11.3964 11.375 11.1208 11.375 10.8335V3.25016L9.75 1.0835H3.25Z" stroke="#B54708" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.625 3.25H11.375" stroke="#B54708" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.66732 5.4165C8.66732 5.99114 8.43904 6.54224 8.03272 6.94857C7.62639 7.3549 7.07529 7.58317 6.50065 7.58317C5.92602 7.58317 5.37492 7.3549 4.96859 6.94857C4.56226 6.54224 4.33398 5.99114 4.33398 5.4165" stroke="#B54708" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 3,
    label: 'Top 10 priority actions this week',
    iconBg: '#EEEDFE',
    iconName: 'benchmark',
    iconStrokeColor: '#534AB7',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M9.75 10.8332V5.4165" stroke="#6366F1" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 10.8332V2.1665" stroke="#6366F1" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.25 10.8335V7.5835" stroke="#6366F1" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 4,
    label: 'How are my locations performing?',
    iconBg: '#F0F9FF',
    iconName: 'location',
    iconStrokeColor: '#0086C9',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M10.8333 5.41659C10.8333 8.1211 7.83303 10.9378 6.82553 11.8078C6.73218 11.8784 6.61814 11.9166 6.5 11.9166C6.38186 11.9166 6.26782 11.8784 6.17448 11.8078C5.16698 10.9378 2.16667 8.1211 2.16667 5.41659C2.16667 4.26732 2.62321 3.16512 3.43586 2.35247C4.24851 1.53982 5.35073 1.08325 6.5 1.08325C7.64927 1.08325 8.75149 1.53982 9.56414 2.35247C10.3768 3.16512 10.8333 4.26732 10.8333 5.41659Z" stroke="#0086C9" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 7.04175C7.39746 7.04175 8.125 6.31421 8.125 5.41675C8.125 4.51929 7.39746 3.79175 6.5 3.79175C5.60254 3.79175 4.875 4.51929 4.875 5.41675C4.875 6.31421 5.60254 7.04175 6.5 7.04175Z" stroke="#0086C9" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 5,
    label: 'Sales trending vs last year by class',
    iconBg: '#ECFDF3',
    iconName: 'chart',
    iconStrokeColor: '#2AAA62',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M11.9173 3.7915L7.31315 8.39567L4.60482 5.68734L1.08398 9.20817" stroke="#2AAA62" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.66602 3.7915H11.916V7.0415" stroke="#2AAA62" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export const myPrompts = [
  { id: 1, label: 'Jewelry vs Accessories sell-through' },
  { id: 2, label: 'OTB pacing vs last month' },
  { id: 3, label: 'Dressy tops reorder analysis' },
];

export const suggestionCards: SuggestionCard[] = [
  {
    id: 1,
    title: "What's my biggest opportunity?",
    description: 'Opportunities & issues',
    badge: 'Urgent',
    iconBg: '#FEF3F2',
    badgeBg: '#FEF3F2',
    badgeColor: '#B42318',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <circle cx="6.5" cy="6.5" r="5" stroke="#B42318" strokeWidth="1.08333" />
        <path d="M6.5 4.3335V6.50016" stroke="#B42318" strokeWidth="1.08333" strokeLinecap="round" />
        <circle cx="6.5" cy="8.6665" r="0.54" fill="#B42318" />
      </svg>
    ),
  },
  {
    id: 2,
    title: 'What should I chase or mark down?',
    description: 'Chase & buy decisions',
    badge: 'Performance',
    iconBg: '#FFFAEB',
    badgeBg: '#FFFAEB',
    badgeColor: '#B54708',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <circle cx="6.5" cy="6.5" r="5" stroke="#B54708" strokeWidth="1.08333" />
        <path d="M6.5 3.7915V9.20817M7.58333 4.87484H5.6875C5.29675 4.87484 4.97917 5.19242 4.97917 5.58317C4.97917 5.97392 5.29675 6.2915 5.6875 6.2915H7.3125C7.70325 6.2915 8.02083 6.60908 8.02083 6.99984C8.02083 7.39059 7.70325 7.70817 7.3125 7.70817H5.41667" stroke="#B54708" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 3,
    title: 'Top-10 priority actions this week',
    description: 'Weekly priority list · planning',
    badge: 'Planning',
    iconBg: '#F9F5FF',
    badgeBg: '#F9F5FF',
    badgeColor: '#6941C6',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M1.625 3.79159L2.70833 4.87492L4.875 2.70825" stroke="#6941C6" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.625 9.20833L2.70833 10.2917L4.875 8.125" stroke="#6941C6" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.04163 3.79163H11.375" stroke="#6941C6" strokeWidth="1.08333" strokeLinecap="round" />
        <path d="M7.04163 9.20837H11.375" stroke="#6941C6" strokeWidth="1.08333" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 4,
    title: 'Sales trending vs last year by class?',
    description: 'Chase & buy decisions',
    badge: 'Buying',
    iconBg: '#ECFDF3',
    badgeBg: '#ECFDF3',
    badgeColor: '#079455',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M1.08337 1.0835H2.38337L3.92171 8.01516C3.97453 8.25439 4.10884 8.46824 4.30154 8.61972C4.49424 8.7712 4.73357 8.85108 4.97837 8.84683H10.1617C10.4065 8.85108 10.6458 8.7712 10.8385 8.61972C11.0312 8.46824 11.1655 8.25439 11.2184 8.01516L12.025 4.3335H2.97504" stroke="#079455" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5.14587" cy="11.1042" r="0.8125" fill="#079455" />
        <circle cx="9.74996" cy="11.1042" r="0.8125" fill="#079455" />
      </svg>
    ),
  },
  {
    id: 5,
    title: "What's my aged inventory look like?",
    description: 'Aged stock & sell-through',
    badge: 'Inventory',
    iconBg: '#F0F9FF',
    badgeBg: '#F0F9FF',
    badgeColor: '#0086C9',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M2.16669 4.3335H10.8334L9.75002 10.8335H3.25002L2.16669 4.3335Z" stroke="#0086C9" strokeWidth="1.08333" strokeLinejoin="round" />
        <path d="M4.33331 4.33333V2.70833C4.33331 2.42102 4.44745 2.14546 4.65061 1.9423C4.85377 1.73914 5.12933 1.625 5.41665 1.625H7.58331C7.87063 1.625 8.14618 1.73914 8.34935 1.9423C8.55251 2.14546 8.66665 2.42102 8.66665 2.70833V4.33333" stroke="#0086C9" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.08331 4.3335H11.9166" stroke="#0086C9" strokeWidth="1.08333" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 6,
    title: "What's my margin trend look like?",
    description: 'Markdown & margin drivers',
    badge: 'Margin',
    iconBg: '#FEF3F2',
    badgeBg: '#FEF3F2',
    badgeColor: '#D92D20',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <circle cx="6.5" cy="6.5" r="5" stroke="#D92D20" strokeWidth="1.08333" />
        <path d="M3.79169 9.20817L9.20835 3.7915" stroke="#D92D20" strokeWidth="1.08333" strokeLinecap="round" />
        <circle cx="4.60417" cy="4.60417" r="0.75" fill="#D92D20" />
        <circle cx="8.39583" cy="8.39583" r="0.75" fill="#D92D20" />
      </svg>
    ),
  },
];

export const defaultActionCards: ActionCard[] = [
  { num: 1, title: 'Chase Dressy Tops with $15K OTB', subtitle: '78% ST · $1,250 above plan', badge: 'Buying', badgeStyle: 'amber' },
  { num: 2, title: 'Mark down Jewelry 20%', subtitle: '31% ST · aging risk', badge: 'Markdown', badgeStyle: 'red' },
  { num: 3, title: 'Follow up on delayed receipts', subtitle: '$11,797 inflow gap', badge: 'Inventory', badgeStyle: 'purple' },
  { num: 4, title: 'Review Accessories position', subtitle: '28% ST · stocking out risk', badge: 'Inventory', badgeStyle: 'purple' },
  { num: 5, title: 'Protect markdown rate', subtitle: '7.3% · 4pts above benchmark', badge: 'Margin', badgeStyle: 'green' },
  { num: 6, title: 'Reorder Denim top 3 SKUs', subtitle: '65% ST · strong LY comp', badge: 'Buying', badgeStyle: 'amber' },
  { num: 7, title: 'Prep May 14 call with Dalia', subtitle: 'Jewelry + Trunk Show strategy', badge: 'Planning', badgeStyle: 'green' },
];

// ─── Inline SVG icons ────────────────────────────────────────────────────────

export const SparkleIcon: React.FC<{ stroke?: string; className?: string }> = ({
  stroke = '#6366F1',
  className,
}) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 2.5C12 7.74671 7.74671 12 2.5 12C7.74671 12 12 16.2533 12 21.5C12 16.2533 16.2533 12 21.5 12C16.2533 12 12 7.74671 12 2.5Z"
      stroke={stroke}
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.5 2.5V6.5M21.5 4.5H17.5"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 17.5V21.5M6.5 19.5H2.5"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const StarIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <g clipPath="url(#c)">
      <path d="M4.80208 0.956246C4.82034 0.919355 4.84855 0.888302 4.88352 0.86659C4.91849 0.844879 4.95883 0.833374 5 0.833374C5.04116 0.833374 5.0815 0.844879 5.11648 0.86659C5.15145 0.888302 5.17965 0.919355 5.19791 0.956246L6.16041 2.90583C6.22382 3.03415 6.31742 3.14517 6.43317 3.22935C6.54893 3.31354 6.68338 3.36838 6.825 3.38916L8.9775 3.70416C9.01828 3.71007 9.0566 3.72728 9.08812 3.75383C9.11964 3.78038 9.14309 3.81522 9.15584 3.85442C9.16858 3.89361 9.17011 3.93558 9.16024 3.97559C9.15037 4.01561 9.12951 4.05206 9.1 4.08083L7.54333 5.59666C7.44067 5.69671 7.36386 5.8202 7.31951 5.95651C7.27516 6.09282 7.26461 6.23787 7.28875 6.37916L7.65625 8.52083C7.66345 8.5616 7.65904 8.60356 7.64354 8.64195C7.62803 8.68033 7.60205 8.71358 7.56856 8.73791C7.53506 8.76224 7.49541 8.77666 7.45411 8.77954C7.41281 8.78241 7.37154 8.77362 7.335 8.75416L5.41083 7.7425C5.28405 7.67592 5.14299 7.64114 4.99979 7.64114C4.85659 7.64114 4.71553 7.67592 4.58875 7.7425L2.665 8.75416C2.62847 8.7735 2.58725 8.7822 2.54602 8.77927C2.50479 8.77634 2.46521 8.7619 2.43179 8.73759C2.39836 8.71328 2.37243 8.68007 2.35694 8.64176C2.34145 8.60344 2.33702 8.56154 2.34416 8.52083L2.71125 6.37958C2.7355 6.23822 2.72499 6.09308 2.68064 5.95668C2.63629 5.82029 2.55942 5.69673 2.45666 5.59666L0.899997 4.08125C0.870245 4.05251 0.849161 4.01599 0.839148 3.97586C0.829135 3.93573 0.830596 3.89359 0.843363 3.85424C0.856131 3.8149 0.879692 3.77993 0.911362 3.75332C0.943033 3.72672 0.98154 3.70954 1.0225 3.70375L3.17458 3.38916C3.31635 3.36854 3.45099 3.31377 3.56691 3.22957C3.68282 3.14538 3.77654 3.03428 3.84 2.90583L4.80208 0.956246Z" stroke="#6941C6" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="c"><rect width="10" height="10" fill="white" /></clipPath></defs>
  </svg>
);

export const ArtifactBarChartIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <g clipPath="url(#a)">
      <path d="M8.75004 1.16675H3.50004C3.19062 1.16675 2.89388 1.28966 2.67508 1.50846C2.45629 1.72725 2.33337 2.024 2.33337 2.33341V11.6667C2.33337 11.9762 2.45629 12.2729 2.67508 12.4917C2.89388 12.7105 3.19062 12.8334 3.50004 12.8334H10.5C10.8095 12.8334 11.1062 12.7105 11.325 12.4917C11.5438 12.2729 11.6667 11.9762 11.6667 11.6667V4.08341L8.75004 1.16675Z" stroke="#B42318" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.16663 1.16675V3.50008C8.16663 3.8095 8.28954 4.10625 8.50833 4.32504C8.72713 4.54383 9.02387 4.66675 9.33329 4.66675H11.6666" stroke="#B42318" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.66663 10.4999V9.33325" stroke="#B42318" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 10.5001V8.16675" stroke="#B42318" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.33337 10.5V7" stroke="#B42318" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="a"><rect width="14" height="14" fill="white" /></clipPath></defs>
  </svg>
);

export const ArtifactListIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1.75 9.91667L2.91667 11.0833L5.25 8.75" stroke="#6941C6" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.75 4.08341L2.91667 5.25008L5.25 2.91675" stroke="#6941C6" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.58337 3.5H12.25" stroke="#6941C6" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.58337 7H12.25" stroke="#6941C6" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.58337 10.5H12.25" stroke="#6941C6" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArtifactCartIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <g clipPath="url(#b)">
      <path d="M4.66671 12.8334C4.98887 12.8334 5.25004 12.5722 5.25004 12.2501C5.25004 11.9279 4.98887 11.6667 4.66671 11.6667C4.34454 11.6667 4.08337 11.9279 4.08337 12.2501C4.08337 12.5722 4.34454 12.8334 4.66671 12.8334Z" stroke="#B54708" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.0833 12.8334C11.4055 12.8334 11.6667 12.5722 11.6667 12.2501C11.6667 11.9279 11.4055 11.6667 11.0833 11.6667C10.7612 11.6667 10.5 11.9279 10.5 12.2501C10.5 12.5722 10.7612 12.8334 11.0833 12.8334Z" stroke="#B54708" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.1958 1.1958H2.36247L3.91413 8.4408C3.97105 8.70614 4.11869 8.94333 4.33163 9.11155C4.54457 9.27977 4.8095 9.3685 5.0808 9.36247H10.7858C11.0513 9.36204 11.3087 9.27105 11.5156 9.10454C11.7224 8.93802 11.8662 8.70594 11.9233 8.44663L12.8858 4.11247H2.98663" stroke="#B54708" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="b"><rect width="14" height="14" fill="white" /></clipPath></defs>
  </svg>
);

export const HistoryLockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
    <g clipPath="url(#clip0_41066_7624)">
      <path d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5M2.5 5.5H9.5C10.0523 5.5 10.5 5.94772 10.5 6.5V10C10.5 10.5523 10.0523 11 9.5 11H2.5C1.94772 11 1.5 10.5523 1.5 10V6.5C1.5 5.94772 1.94772 5.5 2.5 5.5Z" stroke="#667085" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_41066_7624">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const HistorySharedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
    <path d="M9 10.5C9 9.43913 8.57857 8.42172 7.82843 7.67157C7.07828 6.92143 6.06087 6.5 5 6.5M5 6.5C3.93913 6.5 2.92172 6.92143 2.17157 7.67157C1.42143 8.42172 1 9.43913 1 10.5M5 6.5C6.38071 6.5 7.5 5.38071 7.5 4C7.5 2.61929 6.38071 1.5 5 1.5C3.61929 1.5 2.5 2.61929 2.5 4C2.5 5.38071 3.61929 6.5 5 6.5ZM11 9.99998C11 8.31498 10 6.74998 9.00002 5.99998C9.32873 5.75336 9.59158 5.42952 9.76531 5.05711C9.93903 4.6847 10.0183 4.27521 9.99603 3.86488C9.97379 3.45454 9.85073 3.05602 9.63776 2.70458C9.42479 2.35313 9.12847 2.05961 8.77502 1.84998" stroke="#1570EF" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
